import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../api/Api.js";
import { Edit2, X, Save, Search, Calendar, CheckCircle } from "lucide-react";

const AttendanceTable = () => {
  const [driverData, setDriverData] = useState([]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [observationMap, setObservationMap] = useState({});
  const [saveAttendanceList, setSaveAttendanceList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // 🟢 DATE STATE: Get Today in YYYY-MM-DD format
  // 'en-CA' ensures the format is always YYYY-MM-DD which HTML inputs require
  const todayStr = new Date().toLocaleDateString('en-CA'); 
  const [selectedDate, setSelectedDate] = useState(todayStr);

  // Edit Modal State
  const [editingDriver, setEditingDriver] = useState(null);
  const [editPunchIn, setEditPunchIn] = useState("");
  const [editPunchOut, setEditPunchOut] = useState("");

  // INITIAL LOAD
  useEffect(() => {
    fetchDriverData();
  }, []);

  // LOAD DATA ON DATE CHANGE
  useEffect(() => {
    if (selectedDate) {
      setObservationMap({}); 
      fetchAttendance();
      fetchSaveAttendance();
    }
  }, [selectedDate]);

  // SYNC SAVED DATA
  useEffect(() => {
    if (saveAttendanceList.length > 0) {
      const initialMap = {};
      saveAttendanceList.forEach((rec) => {
        if (rec.attendance_json) {
          Object.keys(rec.attendance_json).forEach((driverRegNo) => {
            initialMap[driverRegNo] = rec.attendance_json[driverRegNo];
          });
        }
      });
      setObservationMap(initialMap);
    }
  }, [saveAttendanceList]);

  // --- API CALLS ---
  const fetchDriverData = async () => {
    try {
      const res = await api.get("/adminmod/getDriverData");
      setDriverData(res.data?.data || []);
    } catch { toast.error("Failed to load Driver details"); }
  };

  const fetchAttendance = async () => {
    if (!selectedDate) return;
    const [y, m, d] = selectedDate.split("-");
    try {
      const resp = await api.get(`/adminmod/attendanceGetDateData/${d}/${m}/${y}`);
      setAttendanceList(resp.data?.data || []);
    } catch { toast.error("Failed to load attendance"); }
  };

  const fetchSaveAttendance = async () => {
    if (!selectedDate) return;
    const [y, m, d] = selectedDate.split("-");
    try {
      const resp = await api.get(`/adminmod/saveAttendanceGetDateData/${d}/${m}/${y}`);
      setSaveAttendanceList(resp.data?.data || []);
    } catch { toast.error("Failed to load saved data"); }
  };

  // --- HELPERS ---
  const getAttendanceByDriver = (regNo) => 
    attendanceList.find((att) => att.driver_regno === regNo);

  const getSaveAttendanceByDriver = (regNo) => {
    for (const rec of saveAttendanceList) {
      if (rec.attendance_json?.[regNo]) return rec.attendance_json[regNo];
    }
    return null;
  };

  // --- EDIT HANDLERS ---
  const handleEditClick = (driver) => {
    const att = getAttendanceByDriver(driver.driver_regno);
    
    const getTimeFromDate = (dateStr) => {
       if(!dateStr) return "";
       const date = new Date(dateStr);
       const hours = String(date.getHours()).padStart(2, '0');
       const minutes = String(date.getMinutes()).padStart(2, '0');
       return `${hours}:${minutes}`;
    };

    setEditPunchIn(att?.checkin_datetime ? getTimeFromDate(att.checkin_datetime) : "");
    setEditPunchOut(att?.checkout_datetime ? getTimeFromDate(att.checkout_datetime) : "");
    setEditingDriver(driver);
  };

  const saveManualTime = async () => {
    // 🟢 FIX: GET DYNAMIC ADMIN ID
    const sessionUserId = localStorage.getItem("SessionUserId");
    if (!sessionUserId) {
        toast.error("Session expired. Please login again.");
        return;
    }

    try {
      const payload = {
        driver_regno: editingDriver.driver_regno,
        date: selectedDate,
        punch_in_time: editPunchIn ? editPunchIn + ":00" : "", 
        punch_out_time: editPunchOut ? editPunchOut + ":00" : "",
        admin_id: sessionUserId // 🟢 Uses value from LocalStorage
      };

      await api.post("/adminmod/updatePunchManual", payload);
      toast.success("Time updated successfully!");
      setEditingDriver(null);
      await fetchAttendance(); 
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    }
  };

  const confirmAttendance = async () => {
    const payload = driverData.map((d) => {
      let status = observationMap[d.driver_regno];
      const saved = getSaveAttendanceByDriver(d.driver_regno);
      
      // AUTO LOGIC CALCULATION
      if (status === undefined && !saved) {
         const att = getAttendanceByDriver(d.driver_regno);
         const hasIn = !!att?.checkin_datetime;
         const hasOut = !!att?.checkout_datetime;

         if (hasIn && hasOut) {
            status = "Present";
         } else if (hasIn && !hasOut) {
            status = "Half Day";
         } else {
            status = "Absent"; 
         }
      }

      if (status === undefined) status = saved;

      return {
        driver_regno: d.driver_regno,
        attendance_status: status || "Absent",
        attendance_date: selectedDate,
      };
    });

    try {
      await api.post("/adminmod/attendance/confirm", { records: payload });
      toast.success("Attendance saved successfully");
      fetchSaveAttendance();
    } catch { toast.error("Failed to save"); }
  };

  // --- FILTER & LOGIC ---
  const filteredDrivers = driverData.filter((driver) => {
    const term = searchTerm.toLowerCase();
    const fullName = `${driver.driver_fstname} ${driver.driver_mdlname} ${driver.driver_lstname}`.toLowerCase();
    return fullName.includes(term) || driver.driver_regno.toLowerCase().includes(term);
  });

  const isBackDate = selectedDate < todayStr;

  return (
    // 🟢 MAIN CONTAINER: Fixed Height (vh-100) prevents whole page scroll
    <div className="d-flex flex-column vh-100 bg-white">
      
      {/* 🟢 HEADER SECTION (Fixed) */}
      <div className="bg-white border-bottom shadow-sm p-2 flex-shrink-0">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold text-primary mb-0">Daily Attendance</h4>
            <small className="text-muted">Manage driver punches & status</small>
          </div>
          
          <div className="d-flex gap-2 align-items-center">
             {/* 1. DATE PICKER */}
             <div className="input-group input-group-sm" style={{width: '160px'}}>
                <span className="input-group-text bg-white"><Calendar size={16}/></span>
                <input 
                  type="date" 
                  className="form-control" 
                  value={selectedDate} 
                  max={todayStr}  /* 🟢 FIX: Disables future dates */
                  onChange={e => setSelectedDate(e.target.value)} 
                />
             </div>

             {/* 2. SEARCH */}
             <div className="input-group input-group-sm" style={{width: '200px'}}>
                <span className="input-group-text bg-white"><Search size={16}/></span>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Search..." 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                />
             </div>

             {/* 3. CONFIRM & SAVE BUTTON */}
             <button 
               className="btn btn-primary btn-sm d-flex align-items-center gap-2 px-3 fw-bold" 
               onClick={confirmAttendance}
               style={{height: '31px'}}
             >
               <CheckCircle size={16} /> Save Attendance
             </button>
          </div>
        </div>
      </div>

      {/* 🟢 SCROLLABLE TABLE AREA (Flex Grow) */}
      <div className="flex-grow-1 overflow-auto p-3">
        <div className="card shadow-sm border-0 h-100">
          <div className="card-body p-0">
            <div className="table-responsive h-100"> 
              <table className="table table-hover table-striped align-middle mb-0">
                {/* Sticky Header */}
                <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1020 }}>
                  <tr className="table-secondary shadow-sm">
                    <th className="ps-4 py-3" width="5%">#</th>
                    <th width="20%">Driver Name</th>
                    <th width="10%">Driver ID</th>
                    <th width="10%">Punch In</th>
                    <th width="10%">Punch Out</th>
                    {isBackDate && <th width="5%" className="text-center">Edit</th>}
                    <th width="10%">Status</th>
                    <th width="15%" className="pe-4">Observation</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDrivers.map((emp, index) => {
                    const att = getAttendanceByDriver(emp.driver_regno);
                    const saved = getSaveAttendanceByDriver(emp.driver_regno);
                    
                    let rawStatus = "Absent"; 
                    if (att?.checkin_datetime && att?.checkout_datetime) rawStatus = "Present";
                    else if (att?.checkin_datetime) rawStatus = "Half Day";

                    const currentVal = observationMap[emp.driver_regno] ?? saved ?? rawStatus;

                    const pIn = att?.checkin_datetime ? att.checkin_datetime.split(", ")[1] : "--";
                    const pOut = att?.checkout_datetime ? att.checkout_datetime.split(", ")[1] : "--";

                    return (
                      <tr key={emp.driver_regno}>
                        <td className="ps-4">{index + 1}</td>
                        <td className="fw-bold text-secondary">{emp.driver_fstname} {emp.driver_mdlname} {emp.driver_lstname}</td>
                        <td><span className="badge bg-white text-dark border">{emp.driver_regno}</span></td>
                        
                        <td className={pIn !== "--" ? "text-success fw-bold" : "text-muted"}>{pIn}</td>
                        <td className={pOut !== "--" ? "text-danger fw-bold" : "text-muted"}>{pOut}</td>

                        {isBackDate && (
                          <td className="text-center">
                             <button 
                               className="btn btn-sm btn-outline-primary border-0 rounded-circle p-1" 
                               onClick={() => handleEditClick(emp)}
                             >
                               <Edit2 size={16}/>
                             </button>
                          </td>
                        )}

                        <td>
                          <span className={`badge rounded-pill 
                            ${currentVal === 'Present' ? 'bg-success' : 
                              currentVal === 'Absent' ? 'bg-danger' : 
                              currentVal === 'Half Day' ? 'bg-warning text-dark' : 'bg-secondary'}`
                          }>
                            {currentVal}
                          </span>
                        </td>

                        <td className="pe-4">
                          <select
                            className="form-select form-select-sm shadow-none"
                            value={currentVal}
                            onChange={(e) =>
                              setObservationMap(prev => ({...prev, [emp.driver_regno]: e.target.value}))
                            }
                            style={{borderColor: currentVal === 'Present' ? '#198754' : '#dee2e6'}}
                          >
                            <option value="Present">Present</option>
                            <option value="Absent">Absent</option>
                            <option value="Half Day">Half Day</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 EDIT MODAL */}
      {editingDriver && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Edit Time: {editingDriver.driver_fstname}</h5>
                <button type="button" className="btn-close" onClick={() => setEditingDriver(null)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-primary d-flex align-items-center p-2 mb-3">
                   <Calendar size={16} className="me-2"/>
                   <span className="small">Editing Date: <b>{new Date(selectedDate).toDateString()}</b></span>
                </div>
                
                <div className="row g-3">
                    <div className="col-6">
                        <label className="form-label small fw-bold text-success">Punch In Time</label>
                        <input 
                            type="time" 
                            className="form-control"
                            value={editPunchIn}
                            onChange={(e) => setEditPunchIn(e.target.value)}
                        />
                    </div>
                    <div className="col-6">
                        <label className="form-label small fw-bold text-danger">Punch Out Time</label>
                        <input 
                            type="time" 
                            className="form-control"
                            value={editPunchOut}
                            onChange={(e) => setEditPunchOut(e.target.value)}
                        />
                    </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-light" onClick={() => setEditingDriver(null)}>Cancel</button>
                <button className="btn btn-primary d-flex align-items-center gap-2" onClick={saveManualTime}>
                    <Save size={16} /> Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceTable;